// store/authStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import axios from 'axios';
import toast from 'react-hot-toast';
import { USER_ROLES } from '@/utils/constants';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface User {
  id: string;
  _id?: string;
  name: string;
  email: string;
  role: typeof USER_ROLES.ADMIN | typeof USER_ROLES.TEAM_MEMBER;
  status: 'Active' | 'Inactive' | 'Invited';
  avatar?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  clearAuth: () => void;
  
  register: (data: RegisterData) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  
  // Helper methods for new role system
  isAdmin: () => boolean;
  canManageTask: (task: any) => boolean;
  canEditTaskStatus: (task: any) => boolean;
  canDeleteTask: () => boolean;
  isTeamLeaderForTask: (task: any) => boolean;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  role?: string;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,

        login: async (email: string, password: string, rememberMe = false) => {
          set({ isLoading: true });
          try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
              email,
              password
            });

            const responseData = response.data.data || response.data;
            const { user, accessToken, refreshToken } = responseData;

            if (!user || !accessToken) {
              throw new Error('Invalid response format from server');
            }

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(user));

            if (rememberMe) {
              localStorage.setItem('rememberMe', 'true');
            }

            set({
              user,
              accessToken,
              refreshToken,
              isAuthenticated: true,
              isLoading: false
            });

            toast.success('Login successful!');
            
          } catch (error: any) {
            console.error('Login error details:', error);
            
            set({ isLoading: false });
            const message = error.response?.data?.message || error.message || 'Invalid email or password';
            toast.error(message);
            throw error;
          }
        },

        register: async (data: RegisterData) => {
          set({ isLoading: true });
          try {
            const response = await axios.post(`${API_BASE_URL}/auth/register`, data);
            toast.success('Registration successful! Please login.');
            set({ isLoading: false });
            return response.data;
          } catch (error: any) {
            set({ isLoading: false });
            const message = error.response?.data?.message || 'Registration failed';
            toast.error(message);
            throw error;
          }
        },

        logout: async () => {
          try {
            const refreshToken = get().refreshToken;
            if (refreshToken) {
              await axios.post(`${API_BASE_URL}/auth/logout`, { refreshToken });
            }
          } catch (error) {
            console.error('Logout error:', error);
          } finally {
            get().clearAuth();
            window.location.href = '/login';
            toast.success('Logged out successfully');
          }
        },

        checkAuth: async () => {
          const token = localStorage.getItem('accessToken');
          const storedUser = localStorage.getItem('user');
          
          if (!token) {
            get().clearAuth();
            return;
          }

          if (storedUser && storedUser !== 'undefined') {
            try {
              const user = JSON.parse(storedUser);
              set({
                user,
                accessToken: token,
                refreshToken: localStorage.getItem('refreshToken'),
                isAuthenticated: true,
                isLoading: false
              });
            } catch (e) {
              console.error('Failed to parse stored user:', e);
            }
          }

          set({ isLoading: true });
          try {
            const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            const responseData = response.data.data || response.data;
            
            if (response.data.valid !== false) {
              const userData = responseData.user || responseData;
              
              if (userData) {
                localStorage.setItem('user', JSON.stringify(userData));
                set({
                  user: userData,
                  isAuthenticated: true,
                  isLoading: false
                });
              }
            } else {
              get().clearAuth();
            }
          } catch (error: any) {
            console.error('Auth verification error:', error);
            
            if (error.response?.status === 401) {
              const refreshed = await get().refreshAccessToken();
              if (!refreshed) {
                get().clearAuth();
              }
            } else {
              set({ isLoading: false });
            }
          }
        },

        refreshAccessToken: async () => {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) return false;

          try {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
              refreshToken
            });

            const responseData = response.data.data || response.data;
            const { accessToken, refreshToken: newRefreshToken } = responseData;
            
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);

            set({
              accessToken,
              refreshToken: newRefreshToken
            });

            return true;
          } catch (error) {
            console.error('Token refresh error:', error);
            return false;
          }
        },

        forgotPassword: async (email: string) => {
          try {
            await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email });
            toast.success('Password reset instructions sent to your email');
          } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to send reset email';
            toast.error(message);
            throw error;
          }
        },

        resetPassword: async (token: string, newPassword: string) => {
          try {
            await axios.post(`${API_BASE_URL}/auth/reset-password`, {
              token,
              newPassword
            });
            toast.success('Password reset successful! Please login with your new password.');
          } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to reset password';
            toast.error(message);
            throw error;
          }
        },

        clearAuth: () => {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('auth-store');
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false
          });
        },

        // Helper methods for new role system
        isAdmin: () => {
          const { user } = get();
          return user?.role === USER_ROLES.ADMIN;
        },

        canManageTask: (task) => {
          const { user } = get();
          if (!user || !task) return false;
          
          // Admin can manage all tasks
          if (user.role === USER_ROLES.ADMIN) return true;
          
          // Check if user is team leader for this task
          const teamLeaderId = typeof task.teamLeader === 'object' 
            ? (task.teamLeader._id || task.teamLeader.id) 
            : task.teamLeader;
          
          const userId = user._id || user.id;
          return teamLeaderId === userId;
        },

        canEditTaskStatus: (task) => {
          const { user } = get();
          if (!user || !task) return false;
          
          // Admin can edit all tasks
          if (user.role === USER_ROLES.ADMIN) return true;
          
          // Team leader can edit
          if (get().isTeamLeaderForTask(task)) return true;
          
          // Assigned user can only update status and time
          const assignedUserId = typeof task.allottedTo === 'object' 
            ? (task.allottedTo._id || task.allottedTo.id) 
            : task.allottedTo;
          
          const userId = user._id || user.id;
          return assignedUserId === userId;
        },

        canDeleteTask: () => {
          const { user } = get();
          return user?.role === USER_ROLES.ADMIN;
        },

        isTeamLeaderForTask: (task) => {
          const { user } = get();
          if (!user || !task) return false;
          
          const teamLeaderId = typeof task.teamLeader === 'object' 
            ? (task.teamLeader._id || task.teamLeader.id) 
            : task.teamLeader;
          
          const userId = user._id || user.id;
          return teamLeaderId === userId;
        }
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated
        })
      }
    )
  )
);